import { useState } from 'react';

import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { FeatureType } from 'src/types/subscription.types';
import { useGetSuitableJobsQuery } from 'src/services/ai/ai-enrichment';
import { ApplicationStage } from 'src/services/applications/applications.types';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';
import { ApplicationStageLabel } from 'src/sections/jobs/applications/applications-stage-label';

const scoreColor = (score: number): 'success' | 'warning' | 'default' => {
  if (score >= 70) return 'success';
  if (score >= 55) return 'warning';
  return 'default';
};

/**
 * The agency's open jobs this candidate suits (JT1-1771), from GET /ai/candidates/:id/matching-jobs
 * (JT1-1770): up to five, best first, each with the AI's reason and an Add to job action. A job the
 * candidate is already on shows its stage instead. Hidden for agencies without AI features.
 */
export default function SuitableJobsCard({ candidateId }: { candidateId?: string }) {
  const { canAccess } = useFeatures();
  const enabled = canAccess(FeatureType.AI_SOURCING_ENABLED);
  const { currentData, isFetching, refetch } = useGetSuitableJobsQuery(candidateId as string, { skip: !enabled || !candidateId });
  const [adding, setAdding] = useState<string | null>(null);

  if (!enabled || !candidateId) return null;

  return (
    <Card>
      <CardHeader
        title="Suitable jobs"
        subheader="Your open jobs this candidate fits"
        avatar={<Iconify icon="mdi:robot-outline" width={22} />}
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {isFetching && !currentData && (
          <Stack spacing={1}>
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={48} />)}
          </Stack>
        )}

        {currentData && !currentData.jobs.length && (
          <Typography variant="body2" color="text.secondary">
            {currentData.notice ?? 'None of your open jobs is a good fit for this candidate.'}
          </Typography>
        )}

        <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}>
          {currentData?.jobs.map((job) => (
            <Stack key={job.jobId} spacing={0.5} sx={{ py: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Link component={RouterLink} href={paths.dashboard.jobs.detail(job.jobId)} variant="subtitle2" color="inherit">
                  {job.title}
                </Link>
                <Chip label={job.score} size="small" color={scoreColor(job.score)} sx={{ fontWeight: 700, minWidth: 40 }} />
              </Stack>
              {job.reason && (
                <Typography variant="body2" color="text.secondary">{job.reason}</Typography>
              )}
              <Stack direction="row" sx={{ pt: 0.5 }}>
                {job.onJob ? (
                  <ApplicationStageLabel status={job.onJob.stage as ApplicationStage} />
                ) : (
                  <Button
                    size="small"
                    variant="soft"
                    color="primary"
                    startIcon={<Iconify icon="mdi:invite" width={16} />}
                    onClick={() => setAdding(job.jobId)}
                  >
                    Add to job
                  </Button>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </CardContent>

      <BulkAddCandidatesJobModal
        open={!!adding}
        jobId={adding ?? undefined}
        candidates={[candidateId]}
        onClose={() => setAdding(null)}
        onAdded={() => {
          setAdding(null);
          refetch();
        }}
      />
    </Card>
  );
}
